
import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, FileText } from 'lucide-react';
import { toast } from 'sonner';

interface TextUploaderProps {
  onTextSelected: (file: File) => void;
}

const TextUploader = ({ onTextSelected }: TextUploaderProps) => {
  const [isDragging, setIsDragging] = useState(false);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file && (file.type === 'text/plain' || file.type === 'application/pdf' || file.type === 'text/vtt')) {
      onTextSelected(file);
    } else {
      toast.error('Please upload a text, PDF, or VTT file');
    }
  }, [onTextSelected]);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      'text/plain': ['.txt'],
      'application/pdf': ['.pdf'],
      'text/vtt': ['.vtt']
    },
    multiple: false,
    onDragEnter: () => setIsDragging(true),
    onDragLeave: () => setIsDragging(false),
  });

  return (
    <div
      {...getRootProps()}
      className={`w-full p-8 border-2 border-dashed rounded-lg cursor-pointer transition-colors duration-200 ${
        isDragging
          ? 'border-primary bg-primary/5'
          : 'border-gray-300 hover:border-primary'
      }`}
    >
      <input {...getInputProps()} />
      <div className="flex flex-col items-center justify-center gap-4">
        <div className="p-4 bg-primary/10 rounded-full">
          {isDragging ? (
            <Upload className="w-8 h-8 text-primary" />
          ) : (
            <FileText className="w-8 h-8 text-primary" />
          )}
        </div>
        <div className="text-center">
          <p className="text-lg font-semibold text-gray-700">
            Drop your text document here
          </p>
          <p className="text-sm text-gray-500">
            or click to select (TXT, PDF, VTT)
          </p>
        </div>
      </div>
    </div>
  );
};

export default TextUploader;
